import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, UntypedFormArray, ValidatorFn, Validators } from '@angular/forms';
import { FormControlModel, StepperGroupModel } from '@thehubz/client/core';
import { AnyType } from '@thehubz/shared/model';

@Injectable({
  providedIn: 'root',
})
export class StepperDialogFormService {
  /**
   * Creates a group of form controls based on form fields of the given step
   * @param step
   * @returns FormGroup, chips: string[]
   */
  getFormGroupFor(step: StepperGroupModel): [FormGroup, string[]] {
    const formGroup: FormGroup = new FormGroup({});
    let chips: string[] = [];

    step.formFields?.forEach((formField) => {
      const fieldObject = formField;
      // create form control and add it to the form group
      let fieldValueObject = [];
      if (fieldObject.formFieldName && fieldObject.formFieldName !== '') {
        const fieldName = formField.formFieldName;
        // handle checkbox
        if (fieldObject.fieldType === 'checkbox') {
          if ((fieldObject.value || 'value' in fieldObject) && !fieldObject.options) {
            fieldValueObject = fieldObject.value;
            const control: FormControl = this.createFormFieldControl(fieldObject);
            formGroup.addControl(fieldName, control);
          }
          if (fieldObject.value && fieldObject.options) {
            const control: FormArray = this.buildCheckboxArray(fieldObject, fieldValueObject);
            formGroup.addControl(fieldName, control);
          }
        } else if (fieldObject['fieldType'] == 'formArray') {
          const control: FormArray = this.buildFormArray(fieldObject.formFieldGroup);
          formGroup.addControl(fieldName, control);
        } else {
          if (fieldObject.fieldType === 'matChipList') {
            chips = fieldObject.value || [];
          }
          const control: FormControl = this.createFormFieldControl(fieldObject);
          if (fieldObject.disable) {
            control.disable();
          }
          formGroup.addControl(fieldName, control);
        }
      }
    });

    return [formGroup, chips];
  }

  /**
   * Build form array
   * @param formArray
   * @returns
   */
  buildFormArray(formArray: AnyType): UntypedFormArray {
    const control = new UntypedFormArray([]);
    const groupControl = this.buildFormGroup(formArray);
    control.push(groupControl);
    return control;
  }

  buildFormGroup(formArray: AnyType): FormGroup<AnyType> {
    const groupControl: FormGroup = new FormGroup({});
    formArray.forEach((formField: AnyType) => {
      const fieldName = formField['formFieldName'];
      const control: FormControl = this.createFormFieldControl(formField);
      if (formField['disable']) {
        control.disable();
      }
      groupControl.addControl(fieldName, control);
    });
    return groupControl;
  }

  /**
   * For creating form control from FormControlModel
   * @param fieldObject FormControlModel
   * @returns
   */
  createFormFieldControl(fieldObject: FormControlModel) {
    const fieldValidation: ValidatorFn[] = [];
    if (fieldObject['validators']) {
      if (fieldObject['validators']['required']) {
        fieldValidation.push(Validators.required);
      }
      if (fieldObject['validators']['max']) {
        fieldValidation.push(Validators.max(fieldObject['validators']['max']));
      }
      if (fieldObject['validators']['min']) {
        fieldValidation.push(Validators.min(fieldObject['validators']['min']));
      }
      if (fieldObject['validators']['pattern']) {
        fieldValidation.push(Validators.pattern(fieldObject['validators']['pattern']));
      }
      if (fieldObject['validators']['minLength']) {
        fieldValidation.push(Validators.minLength(fieldObject['validators']['minLength']));
      }
      if (fieldObject['validators']['maxLength']) {
        fieldValidation.push(Validators.maxLength(fieldObject['validators']['maxLength']));
      }
    }
    let fieldPatchValue: AnyType = '';
    if (fieldObject.fieldType === 'checkbox') {
      fieldPatchValue = false;
    }
    if (Object.prototype.hasOwnProperty.call(fieldObject, 'value')) {
      if (fieldObject.fieldType === 'checkbox') {
        fieldPatchValue = fieldObject['value'] ? fieldObject['value'] : false;
      } else {
        fieldPatchValue = fieldObject['value'];
      }
    }
    return new FormControl(fieldPatchValue, fieldValidation as ValidatorFn[]);
  }

  /**
   * Create checkbox array
   * @param formArray
   * @param buildFormArray
   * @returns
   */
  buildCheckboxArray(formArray: FormControlModel, buildFormArray?: AnyType[]): FormArray<FormControl<AnyType>> {
    const arr =
      formArray?.options?.map((option) => {
        if (buildFormArray && buildFormArray.length > 0 && buildFormArray.includes(option.id)) {
          option.selected = true;
        }
        return new FormControl(option.selected ? option.value : null);
      }) || [];
    return new FormArray(arr);
  }

  /**
   * Validate the length of an input field
   * Returns true if valid, otherwise false
   */
  validateLength(minLength = 0, maxLength = 0, value = '') {
    // validate min length
    if (minLength > 0 && value.length < minLength) {
      return false;
    }
    // validate max length
    if (maxLength > 0 && value.length > maxLength) {
      return false;
    }
    return true;
  }
}
